<template>
  <div class="member-info">
    <div class="d-flex flex-wrap" style="gap: 40px;">
      <ImageWithBlurHash
        v-if="member?.account?.avatar"
        :imageSrc="accountAvatar"
        :blurhash="accountBlurHash"
        :width="100"
        :height="100"
        alt="Default"
        :timeoutBlurHash="100"
      />
      <div class="d-flex flex-column justify-content-start">
        <div class="d-flex align-items-center mb-3 cursor-pointer">
          <div class="d-flex align-items-center" @click="copy(`${member.account.login}`, 'Login copied to clipboard')">
            <span class="d-block font-weight-bold font-size-18 mr-1">Login:</span>
            <span class="font-size-18">{{ member.account.login }}</span>
          </div>
          <div class="d-flex align-items-center ml-3">
            <a-badge
              :count="getStatusName(member.status)"
              style="cursor: default; transform: scale(1.2);"
              :number-style="{
                backgroundColor: getBackgroundStatus(member.status),
                boxShadow: '0 4px 8px 0 #d9d9d9',
              }"
            />
          </div>
        </div>
        <a-tooltip
          v-if="isAffiliate"
          placement="bottomRight"
          :get-popup-container="getPopupContainer"
        >
          <template slot="title">
            <span>Link for inviting referrals</span>
          </template>
          <div class="d-flex align-items-center">
            <div class="copy-icon font-size-16 mr-2" @click="copy(`${member.ref_url}`, 'Referral link copied to clipboard')">
              <a-icon type="link" />
            </div>
            <a :href="member.ref_url" target="_blank">{{ member.ref_url }}</a>
          </div>
        </a-tooltip>
      </div>
    </div>
    <div class="mt-5">
      <FormSubTitle title="Info" />
      <div class="d-flex flex-wrap" style="gap: 20px;">
        <InfoBlock icon="user" title="Role" :value="capitalizeRoleName">
          <template #action>
            <a-tooltip v-if="member.started_at" placement="topLeft" arrow-point-at-center>
              <template slot="title">
                <span>Start date</span>
              </template>
              {{ formatDate(member.started_at) }}
            </a-tooltip>
          </template>
        </InfoBlock>
        <InfoBlock icon="phone" title="Phone number" :value="accountPhoneNumber" />
        <InfoBlock icon="dollar" title="Bets amount" :value="member.bets_amount" />
        <InfoBlock
          icon="usergroup-add"
          title="Team members"
          :value="isAffiliate ? `${currentCountTeamMembers} / ${requiredCountTeamMembers}` : 1"
        >
          <template #action>
            <a-icon type="eye" class="eye-icon" @click="goToTable" />
          </template>
        </InfoBlock>
      </div>
    </div>
    <div class="member-info__container mt-5">
      <div class="d-flex flex-column w-full" style="max-width: 500px;">
        <FormSubTitle title="Conditions" />
        <div class="member-info__conditions">
          <Condition
            v-for="(condition, index) in member.conditions"
            :key="index"
            :isOnlyView="true"
            :editPermission="false"
            :languagesUsed="languagesUsed"
            :condition="condition"
            :conditionValue="getConditionValue(condition)"
            @show="toggleShowConditionModal(true, index, condition)"
          />
        </div>
      </div>
      <div class="member-info__cashback">
        <FormSubTitle title="Cashback settings" />
        <div class="d-flex flex-column">
          <SemicircleDiagram :amount="member.cashback.sum" :percentage="member.cashback.percent" />
          <span class="d-block my-2">This is the current cashback amount a user <br>can earn by completing the sprint conditions.</span>
          <div class="d-flex flex-column w-fit" style="gap: 10px;">
            <div class="d-flex" style="gap: 10px;">
              <div class="member-info__block">
                <span class="text-default font-weight-bold">Percentage</span>
                <span class="font-size-18 font-weight-bold">{{ member.settings.cashback_percent }}</span>
              </div>
              <div class="member-info__block">
                <span class="text-default font-weight-bold">Max Amount</span>
                <span class="font-size-18 font-weight-bold">{{ member.settings.cashback_limit }}</span>
              </div>
            </div>
            <div
              class="ant-btn ant-btn-lg d-flex justify-content-between align-items-center w-full h-full text-white cursor-pointer"
              style="background-color: #323232;"
              @click="toggleShowCalculationDrawer(true)"
            >
              <span>View Calculation Details</span>
              <a-icon v-if="memberCashbackInfoLoading" type="loading" class="font-size-12" />
              <a-icon v-else type="right" class="font-size-12" />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="mt-5">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <FormSubTitle :title="isAffiliate ? 'Invited referrals' : 'Affiliate'" :indent="false" />
        <a-tooltip
          placement="top"
          arrow-point-at-center
          :get-popup-container="getPopupContainer"
          >
          <template v-if="completedTeamMembers || isDisabledProgram" slot="title">
            <span>{{ addParticipantsTooltipText }}</span>
          </template>
          <a-button
            v-if="createPermission && isAffiliate"
            type="primary"
            size="large"
            :disabled="!createPermission || completedTeamMembers || isDisabledProgram"
            @click="toggleShowAddReferralModal(true)"
          >
            <a-icon type="plus" /> Add
          </a-button>
        </a-tooltip>
      </div>
      <MemberInfoTable ref="table" :role="roleSystemName" />
    </div>
    <ConditionModal
      v-if="showConditionModal"
      :show="showConditionModal"
      :languagesUsed="languagesUsed"
      :selectedCondition="targetCondition"
      :targetConditionValue="targetConditionValue"
      :isOnlyView="true"
      @close="toggleShowConditionModal"
    />
    <AddUserModal
      v-if="showAddReferralModal"
      :show="showAddReferralModal"
      roleSystemName="referral"
      :parentId="affiliateId"
      @close="toggleShowAddReferralModal"
      @updateTable="updateMembersTable"
    />
    <CashbackCalculationDrawer
      :visible="showCalculationDrawer"
      :info="memberCashbackInfo"
      :memberStatus="member?.status"
      :isAffiliate="isAffiliate"
      @close="toggleShowCalculationDrawer"
    />
  </div>
</template>

<script>
import apiClient from '@/services/axios'
import InfoBlock from './InfoBlock.vue'
import FormSubTitle from '@/components/custom/form/FormSubTitle.vue'
import Condition from '@/views/apps/referral-program/referral-programs/components/Condition.vue'
import ConditionModal from '@/views/apps/referral-program/referral-programs/components/ConditionModal.vue'
import SemicircleDiagram from '@/views/apps/referral-program/components/SemicircleDiagram.vue'
import ImageWithBlurHash from '@/components/custom/image/ImageWithBlurHash.vue'
import MemberInfoTable from '@/views/apps/referral-program/referral-programs/members/components/MemberInfoTable.vue'
import AddUserModal from '@/views/apps/referral-program/referral-programs/members/components/AddUserModal.vue'
import CashbackCalculationDrawer from '@/views/apps/referral-program/referral-programs/members/components/CashbackCalculationDrawer.vue'
import { copyToClipboard } from '@/composables/copy/copyToClipboard.js'
import { capitalizeFirstLetter } from '@/composables/useCapitalizeFirstLetter.js'
import { getMembersStatusColors } from '@/views/apps/referral-program/referral-programs/composables/useGetMembersStatusColors.js'
import { getErrorMessage } from '@/views/apps/fortune-wheel/composables/useGetErrorMessage.js'

const referralApiUrl = process.env.VUE_APP_REFERRAl_API_URL

export default {
  props: {
    createPermission: {
      type: Boolean,
      default: false,
    },
    member: {
      type: Object,
      default: () => {},
    },
    program: {
      type: Object,
      default: () => {},
    },
  },
  components: {
    InfoBlock,
    FormSubTitle,
    Condition,
    SemicircleDiagram,
    MemberInfoTable,
    ImageWithBlurHash,
    ConditionModal,
    AddUserModal,
    CashbackCalculationDrawer,
  },
  data: () => ({
    targetCondition: null,
    showConditionModal: false,
    showAddReferralModal: false,
    memberCashbackInfo: null,
    memberCashbackInfoLoading: false,
    showCalculationDrawer: false,
  }),
  computed: {
    programId() {
      return this.$route.params.id
    },
    roleSystemName() {
      return this.$route.params.role
    },
    memberId() {
      return this.$route.params.member_id
    },
    isAffiliate() {
      return this.roleSystemName === 'affiliate'
    },
    affiliateId() {
      if (!this.isAffiliate) {
        return null
      }
      return this.member?.id || null
    },
    isDisabledProgram() {
      if (!this.program) {
        return true
      }

      return !['active', 'inactive'].includes(this.program.status)
    },
    accountAvatar() {
      return this.member.account?.avatar
        ? this.member.account.avatar
        : '/resources/images/default-square-image.svg'
    },
    accountBlurHash() {
      return this.member?.account?.avatar_blurhash
    },
    accountPhoneNumber() {
      if (!this.member?.account?.phone) {
        return 'Not specified'
      }
      return this.member.account.phone
    },
    addParticipantsTooltipText() {
      if (this.isDisabledProgram) {
        return 'Users cannot be added to the programm with such status'
      }
      return 'Maximum number of referrals reached'
    },
    currentCountTeamMembers() {
      return this.member?.team_members?.current_count || 0
    },
    requiredCountTeamMembers() {
      return this.member?.team_members?.required_count || 0
    },
    completedTeamMembers() {
      return this.currentCountTeamMembers >= this.requiredCountTeamMembers
    },
    capitalizeRoleName() {
      return capitalizeFirstLetter(this.roleSystemName)
    },
    languagesUsed() {
      return this.program.languages.map(lang => {
        return {
          name: lang.name,
          value: lang.id,
          is_default: lang.is_default,
        }
      })
    },
    targetConditionValue() {
      if (!this.targetCondition) {
        return ''
      }
      return this.getConditionValue(this.targetCondition)
    },
  },
  methods: {
    getPopupContainer() {
      return document.body
    },
    formatDate(date) {
      return this.$moment(date).tz(this.$moment.tz.guess()).format('DD.MM.YYYY')
    },
    copy(value, message) {
      copyToClipboard(value, message)
    },
    getStatusName(status) {
      return capitalizeFirstLetter(status)
    },
    getBackgroundStatus(status) {
      return getMembersStatusColors(status)
    },
    getConditionValue(condition) {
      switch (condition?.type) {
        case 'join_program':
          return null
        case 'invite_referrals':
          return this.program['affiliate_settings'].invited_referrals
        case 'referral_bet_sum':
          return this.program['referral_settings']?.bet_sum
        case 'affiliate_bet_sum':
          return this.program['affiliate_settings']?.bet_sum
        default:
          return null
      }
    },
    goToTable() {
      const element = this.$refs['table'].$el
      const top = element.offsetTop
      window.scrollTo({
        top: top,
        behavior: 'smooth',
      })
    },
    updateMembersTable() {
      this.$refs['table'].getParticipants()
      this.member.team_members.current_count += 1
    },
    toggleShowConditionModal(status = false, conditionIndex = null, condition = null) {
      this.targetCondition = { ...condition, system_name: this.roleSystemName, index: conditionIndex }
      this.showConditionModal = status
    },
    toggleShowAddReferralModal(status = false) {
      this.showAddReferralModal = status
    },
    async getMemberCashbackInfo() {
      try {
        this.memberCashbackInfoLoading = true

        const url = `${referralApiUrl}/referral-programs/${this.programId}/members/${this.memberId}/cashback`
        const response = await apiClient.get(url)

        const { data } = response?.data
        if (data) {
          this.memberCashbackInfo = data
          return
        }
        getErrorMessage(response?.data)
      } catch (e) {
        this.$notification.error({
          message: e?.message ? e.message : 'Error',
        })
      } finally {
        this.memberCashbackInfoLoading = false
      }
    },
    async toggleShowCalculationDrawer(status = false) {
      if (!status) {
        this.showCalculationDrawer = status
        return
      }
      await this.getMemberCashbackInfo()
      this.showCalculationDrawer = status
    },
  },
}
</script>

<style lang="scss">
.member-info {
  &__container {
    display: flex;
    flex-wrap: wrap;
    gap: 200px;
    @media (max-width: 768px) {
      gap: 20px;
    }
  }
  &__conditions {
    max-width: 500px;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 20px;
  }
  &__cashback {
    max-width: 500px;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;
    flex: 1 1 auto;
  }
  &__block {
    padding: 10px 20px;
    border: 1px solid #e4e9f0;
    border-radius: 5px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    width: fit-content;
    transition: all .3s ease;
  }
  .copy-icon {
    width: 25px;
    height: 25px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 4px;
    color: rgba(24, 144, 255, 0.7);
    transition: all 0.3s ease;
    &:hover {
      cursor: pointer;
      border: 1px solid #1890FF;
      & i {
        color: #1890FF;
      }
    }
  }
  .eye-icon {
    font-size: 18px;
    color: rgba(24, 144, 255, 0.7);
    &:hover {
      color: #1890FF;
      cursor: pointer;
    }
  }
}
</style>
